import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Error from "../../ErrorPages/Error";
import DashboardFilterModal from "./DashboardFilterModal";
import useScrollPosition from "../../hooks/useScrollPosition";

const getProgressColor = (progress) => {
    if (progress <= 30) return "bg-danger";
    if (progress <= 60) return "bg-warning";
    if (progress <= 90) return "bg-info";
    return "bg-success";
};

const getDeadlineStatus = (deadline, isCompleted) => {
    const today = moment().startOf("day");
    const targetDate = moment(deadline).startOf("day");
    const daysLeft = targetDate.diff(today, "days");

    if (isCompleted) {
        return {
            text: `completed`,
            className: "bg-success",
        };
    }

    if (daysLeft < 0) {
        return {
            text: `Overdue by ${Math.abs(daysLeft)} days`,
            className: "bg-danger",
        };
    }
    if (daysLeft === 0) {
        return { text: "Due today", className: "bg-warning" };
    }
    if (daysLeft === 1) {
        return { text: "Due tomorrow", className: "bg-warning" };
    }
    if (daysLeft <= 5) {
        return { text: `${daysLeft} Days Left`, className: "bg-primary" };
    }
    return { text: `${daysLeft} Days Left`, className: "bg-success" };
};

const DeadlineBadge = ({ deadline, isCompleted }) => {
    const { text, className } = getDeadlineStatus(deadline, isCompleted);

    return (
        <span className={`badge badge-dim ${className}`}>
            <em className="icon ni ni-clock"></em>
            <span>{text}</span>
        </span>
    );
};

const ProgressBar = ({ progress }) => {
    return (
        <div className="project-progress">
            <div className="project-progress-details">
                <div className="project-progress-percent">
                    {`${progress?.toFixed(2)}%`}
                </div>
            </div>
            <div className="progress bg-light" role="progressbar">
                <div
                    className={`progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(
                        progress
                    )}`}
                    style={{ width: `${progress?.toFixed(2)}%` }}
                ></div>
            </div>
        </div>
    );
};

const StackedProgressBar = ({ boAssigned, dpComplete, completedBo }) => {
    let completedDP = 0;
    if (boAssigned < dpComplete) {
        completedDP = (boAssigned / dpComplete) * 100;
    } else {
        completedDP = (dpComplete / boAssigned) * 100;
    }
    const assignedBO = boAssigned - completedDP;
    if (completedBo >= 100) {
        return (
            <div className="progress-stacked bg-light">
                <div
                    className="progress"
                    style={{ width: `${completedBo?.toFixed(2)}%` }}
                    title={`completed daily plans - ${completedBo?.toFixed(
                        2
                    )}%`}
                >
                    <div className="progress-bar progress-bar-striped progress-bar-animated bg-success"></div>
                </div>
            </div>
        );
    }
    return (
        <div className="progress-stacked bg-light">
            <div
                className="progress"
                style={{ width: `${completedDP?.toFixed(2)}%` }}
                title={`completed daily plans - ${dpComplete?.toFixed(2)}%`}
            >
                <div className="progress-bar progress-bar-striped progress-bar-animated bg-success"></div>
            </div>
            <div
                className="progress"
                style={{ width: `${assignedBO?.toFixed(2)}%` }}
                title={`alloted build orders - ${boAssigned?.toFixed(2)}%`}
            >
                <div className="progress-bar progress-bar-striped progress-bar-animated bg-warning"></div>
            </div>
        </div>
    );
};

const BOStatusDropdown = ({ bo_list }) => {
    return (
        <div className="btn-group dropend">
            <a
                className="link dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                + more
            </a>
            <ul
                className="dropdown-menu dropdown-menu-end overflow-auto"
                style={{ maxHeight: "300px" }}
            >
                {bo_list?.map((bo) => (
                    <li>
                        <div className="dropdown-item">
                            <p className="mb-1">
                                <b>
                                    {bo?.item_name} -{" "}
                                    {bo?.build_order_completion_percentage?.toFixed(
                                        2
                                    )}
                                    %
                                </b>
                            </p>
                            <StackedProgressBar
                                boAssigned={
                                    bo?.build_order_assignment_percentage
                                }
                                dpComplete={
                                    bo?.daily_plan_completion_percentage
                                }
                                completedBo={
                                    bo?.build_order_completion_percentage
                                }
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const BOStatusCard = ({ bo_list }) => {
    return (
        <div className="card pt-2">
            <div className="card-header bg-white p-0 px-1">
                <h6>Build Orders</h6>
            </div>
            <div className="card-body p-0 px-3">
                {bo_list?.slice(0, 3)?.map((bo) => (
                    <div className="my-2">
                        <p className="mb-1" style={{ fontSize: "0.8rem" }}>
                            <b>
                                {bo?.item_name} -{" "}
                                {bo?.build_order_completion_percentage?.toFixed(
                                    2
                                )}
                                %
                            </b>
                        </p>
                        <StackedProgressBar
                            boAssigned={bo?.build_order_assignment_percentage}
                            dpComplete={bo?.daily_plan_completion_percentage}
                            completedBo={bo?.build_order_completion_percentage}
                        />
                    </div>
                ))}
                {bo_list?.length > 3 && (
                    <BOStatusDropdown bo_list={bo_list.slice(3)} />
                )}
            </div>
        </div>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { position } = useScrollPosition(100, 100);
    const initialPageCount = Number(searchParams.get("page")) || 1;
    const [salesOrderId, setSalesOrderId] = useState(null);
    const { useInfiniteFetch } = useAPIQuery({
        key: "work_order",
        endpoint: "/api/work-order/",
    });

    const {
        data,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isLoading,
        isFetching,
        isError,
        status,
        isFetchingNextPage,
        isFetchingPreviousPage,
    } = useInfiniteFetch({
        params: {
            action: "job_card",
            sales_order_id: salesOrderId ? Number(salesOrderId) : null,
        },
        staleTime: 300000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 3,
        keepPlaceholder: true,
        initialPageParam: initialPageCount,
        abortOnNewRequest: true,
    });

    const currentPage = data?.pages?.[data.pages.length - 1]?.current_page;

    console.log(data);
    console.log(status);
    console.log(hasPreviousPage);
    console.log(position);

    const jobCards = useMemo(() => {
        return data?.pages.flatMap((page) => page.results.data) ?? [];
    }, [data]);

    console.log(jobCards);

    useEffect(() => {
        // if (position === "top" && hasPreviousPage) {
        //     fetchPreviousPage();
        // } else
        if (position === "bottom" && hasNextPage) {
            fetchNextPage();
        }
    }, [position]);

    useEffect(() => {
        if (currentPage && currentPage !== 1) {
            setSearchParams({
                page: currentPage,
            });
        }
    }, [currentPage]);

    if (isError) return <Error />;

    if (isLoading) return "Loading...";

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h4 className="nk-block-title">Job Cards</h4>
                    </div>
                    {isFetching ? (
                        <div
                            className="spinner-border text-primary ms-2"
                            role="status"
                        >
                            <span className="sr-only"></span>
                        </div>
                    ) : (
                        ""
                    )}
                    <div className="d-flex align-items-center justify-content-center ms-auto">
                        <button
                            className="btn btn-icon btn btn-outline-primary rounded-pill me-3"
                            data-bs-toggle="modal"
                            data-bs-target="#dashboard-filter"
                        >
                            <em className="icon ni ni-filter-fill" />
                        </button>
                    </div>
                </div>
            </div>
            <div className="nk-block">
                {hasPreviousPage ? (
                    <div className="row g-gs justify-content-center py-3">
                        <div className="col-2">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => fetchPreviousPage()}
                                disabled={isFetchingPreviousPage}
                            >
                                {isFetchingPreviousPage
                                    ? "Loading..."
                                    : "Load Prev"}
                            </button>
                        </div>
                    </div>
                ) : null}
                <div className="row g-gs">
                    {jobCards?.length !== 0 ? (
                        jobCards?.map((card) => (
                            <div className="col-sm-6 col-xl-4">
                                <div className="card h-100">
                                    <div className="card-inner">
                                        <div className="project">
                                            <div className="project-head">
                                                <a className="project-title">
                                                    <div className="user-avatar sq bg-purple">
                                                        <span>WO</span>
                                                    </div>
                                                    <div className="project-info">
                                                        <h6
                                                            className="title"
                                                            style={{
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() =>
                                                                navigate(
                                                                    "/job-cards-details",
                                                                    {
                                                                        state: {
                                                                            id: card?.id,
                                                                            work_order_code:
                                                                                card?.work_order_code,
                                                                        },
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            {
                                                                card?.work_order_code
                                                            }
                                                        </h6>
                                                        <span className="sub-text">
                                                            Target Quantity:{" "}
                                                            {
                                                                card?.target_quantity
                                                            }
                                                        </span>
                                                    </div>
                                                </a>
                                            </div>
                                            <ProgressBar
                                                progress={
                                                    card?.build_orders
                                                        ?.work_order_completion_percentage
                                                }
                                            />
                                            {card?.build_orders
                                                ?.completion_percentages &&
                                            card?.build_orders
                                                ?.completion_percentages
                                                ?.length !== 0 ? (
                                                <div className="project-details">
                                                    <BOStatusCard
                                                        bo_list={
                                                            card?.build_orders
                                                                ?.completion_percentages
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                            <div className="project-meta">
                                                {card?.dispatch_date ? (
                                                    <DeadlineBadge
                                                        deadline={moment(
                                                            card?.dispatch_date
                                                        )}
                                                        isCompleted={
                                                            card?.build_orders
                                                                ?.work_order_completion_percentage ===
                                                            100
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-sm-6 col-xl-4">
                            No data to display.
                        </div>
                    )}
                </div>
                {hasNextPage ? (
                    <div className="row g-gs justify-content-center py-3">
                        <div className="col-2">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                            >
                                {isFetchingNextPage
                                    ? "Loading..."
                                    : "Load More"}
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
            <DashboardFilterModal setSalesOrderId={setSalesOrderId} />
        </>
    );
};

export default Dashboard;
